import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { 
  FolderKanban, 
  Users, 
  FileText, 
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";
import ExpiringContractsWidget from "@/components/ExpiringContractsWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/projects`),
        ]);
        setStats(statsRes.data);
        setRecentProjects(projectsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Mock chart data
  const chartData = [
    { month: "Jan", revenue: 45000 },
    { month: "Feb", revenue: 52000 },
    { month: "Mar", revenue: 48000 },
    { month: "Apr", revenue: 61000 },
    { month: "May", revenue: 55000 },
    { month: "Jun", revenue: 67000 },
  ];

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title={t('nav.dashboard')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title={t('nav.dashboard')}>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Expiring contracts alert (only shows if there are any) */}
        <ExpiringContractsWidget />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg">
                  <FolderKanban className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                    {stats?.total_projects || 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('projects.allProjects')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#FF5F1F]/10 rounded-lg">
                  <Clock className="w-6 h-6 text-[#FF5F1F]" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#FF5F1F]">
                    {stats?.active_projects || 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('dashboard.activeProjects')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <Users className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-purple-500">
                    {stats?.total_workers || 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('nav.workers')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {(stats?.total_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('dashboard.totalEarnings')} (NOK)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {(stats?.pending_approvals > 0 || stats?.unpaid_invoices > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats?.pending_approvals > 0 && (
              <Link to="/admin/workers">
                <Card className="bg-[#FF5F1F]/10 border-[#FF5F1F]/30 hover:border-[#FF5F1F] transition-colors cursor-pointer">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-[#FF5F1F]" />
                      <span className="text-white">
                        {stats.pending_approvals} worker(s) pending approval
                      </span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#FF5F1F]" />
                  </CardContent>
                </Card>
              </Link>
            )}

            {stats?.unpaid_invoices > 0 && (
              <Link to="/admin/invoices">
                <Card className="bg-yellow-500/10 border-yellow-500/30 hover:border-yellow-500 transition-colors cursor-pointer">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-yellow-400" />
                      <span className="text-white">
                        {stats.unpaid_invoices} unpaid invoice(s)
                      </span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-yellow-400" />
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        )}

        {/* Charts & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-['Unbounded']">{t('dashboard.overview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="month" 
                      stroke="#94A3B8" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="#94A3B8" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${value / 1000}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#0A0A0A", 
                        border: "1px solid #27272A",
                        borderRadius: "8px"
                      }}
                      labelStyle={{ color: "#94A3B8" }}
                      formatter={(value) => [`${value.toLocaleString()} NOK`, "Revenue"]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#23F7DD" 
                      strokeWidth={2}
                      dot={{ fill: "#23F7DD", strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Projects */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-['Unbounded']">{t('dashboard.recentActivity')}</CardTitle>
              <Link to="/admin/projects" className="text-[#23F7DD] hover:underline text-sm">
                {t('dashboard.viewAll')}
              </Link>
            </CardHeader>
            <CardContent>
              {recentProjects.length === 0 ? (
                <div className="text-center py-8">
                  <FolderKanban className="w-10 h-10 text-[#475569] mx-auto mb-3" />
                  <p className="text-[#94A3B8]">{t('projects.noProjects')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentProjects.map((project) => (
                    <Link
                      key={project.project_id}
                      to={`/admin/projects`}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{project.title}</p>
                        <p className="text-sm text-[#94A3B8]">{project.location_city}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === "completed" || project.status === "paid"
                            ? "bg-green-500/20 text-green-400"
                            : project.status === "in_progress"
                            ? "bg-orange-500/20 text-orange-400"
                            : "bg-blue-500/20 text-blue-400"
                        }`}
                      >
                        {project.status?.replace("_", " ")}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
